import React from 'react';

import type {Class} from '../../../generated/types';
import {Crumb, EntitySeo, composeDescription, entityTitle, pageTitle, withOriginal} from '../../../lib/seo';
import {buildUrl, RouteName, useRoute} from '../../../lib/routes';
import {classDescription, classGenitive, classLead, classSections, SECTION_ROUTES, subClassesLabel, subClassesTitle} from './class-seo-text';

type Tab = {title: string; description: string; path: string; crumb?: string};

/** Какую вкладку класса показывает адрес; null — открыт отдельный пункт, его SEO рисует FeatureSeo */
function classTab(heroClass: Class, routeName: RouteName | null): Tab | null {
	const id = heroClass.id;
	const name = withOriginal(heroClass.title, heroClass.originalTitle);
	const genitive = classGenitive(heroClass);

	switch (routeName) {
		case 'classInfo':
			return {
				title: pageTitle(`${name}: описание класса`),
				description: composeDescription(`Описание класса ${heroClass.title}.`, heroClass.description),
				path: buildUrl('classInfo', {id}),
				crumb: 'Описание',
			};
		case 'subClasses':
			return {
				title: pageTitle(subClassesTitle(id, genitive)),
				description: composeDescription(`${subClassesLabel(id)} класса ${heroClass.title}.`, heroClass.subClassDescription),
				path: buildUrl('subClasses', {id}),
				crumb: 'Подклассы',
			};
		case 'classSpells':
			return {
				title: pageTitle(`Заклинания ${genitive}`),
				description: `Список заклинаний класса ${heroClass.title}, упорядоченный по кругам.`,
				path: buildUrl('classSpells', {id}),
				crumb: 'Заклинания',
			};
	}

	const featureRoutes = Object.values(SECTION_ROUTES).map((routes) => routes.feature);
	if (routeName === 'classFeature' || (routeName && featureRoutes.includes(routeName))) return null;

	const current = classSections(heroClass).find((item) => SECTION_ROUTES[item.type]?.list === routeName);
	if (current) {
		return {
			title: pageTitle(`${current.title} ${genitive}`),
			description: composeDescription(`${current.title} класса ${heroClass.title}.`, current.description),
			path: buildUrl(SECTION_ROUTES[current.type].list, {id}),
			crumb: current.title,
		};
	}

	return {
		title: entityTitle(heroClass.title, heroClass.originalTitle, 'класс'),
		description: classDescription(heroClass),
		path: buildUrl('class', {id}),
	};
}

export const ClassSeo: React.FC<{heroClass: Class}> = ({heroClass}) => {
	const {routeName} = useRoute();
	const tab = classTab(heroClass, routeName);
	if (!tab) return null;

	const classPath = buildUrl('class', {id: heroClass.id});
	const crumbs: Crumb[] = [{name: heroClass.title, path: classPath}];
	if (tab.crumb) crumbs.push({name: tab.crumb, path: tab.path});

	return (
		<EntitySeo
			entity={heroClass}
			section="classes"
			kind="класс"
			title={tab.title}
			description={tab.description}
			path={tab.path}
			og={{kind: 'class'}}
			crumbs={crumbs}
		/>
	);
};
